"use client"

import { Popover, PopoverPanel, Transition } from "@headlessui/react"
import useToggleState from "@lib/hooks/use-toggle-state"
import { BrandLink, CategoryNode } from "@lib/util/category-tree"
import { ArrowRightMini, XMark } from "@medusajs/icons"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { Text, clx } from "@modules/common/components/ui"
import { Fragment, useState } from "react"
import CountrySelect from "../country-select"
import LanguageSelect from "../language-select"
import { Locale } from "@lib/data/locales"

type SideMenuProps = {
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
  categories: CategoryNode[]
  brands: BrandLink[]
}

const sectionTitle = "mb-3 text-sm text-brand-on-ink"

const SideMenu = ({
  regions,
  locales,
  currentLocale,
  categories,
  brands,
}: SideMenuProps) => {
  const countryToggleState = useToggleState()
  const languageToggleState = useToggleState()
  // Which main category has its sub-categories showing. One at a time keeps
  // the menu short enough to read on a phone.
  const [expanded, setExpanded] = useState<string | null>(null)

  return (
    <div className="h-full">
      <div className="flex items-center h-full">
        <Popover className="h-full flex">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  data-testid="nav-menu-button"
                  className="relative h-full flex items-center transition-all ease-out duration-200 focus:outline-none hover:text-ui-fg-base"
                >
                  القائمة
                </Popover.Button>
              </div>

              {open && (
                <div
                  className="fixed inset-0 z-[50] bg-black/0 pointer-events-auto"
                  onClick={close}
                  data-testid="side-menu-backdrop"
                />
              )}

              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0"
                enterTo="opacity-100 backdrop-blur-2xl"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 backdrop-blur-2xl"
                leaveTo="opacity-0"
              >
                <PopoverPanel className="flex flex-col absolute w-full pe-4 sm:pe-0 sm:w-1/3 2xl:w-1/4 sm:min-w-[340px] h-[calc(100vh-1rem)] z-[51] end-0 text-sm text-ui-fg-on-color m-2 backdrop-blur-2xl">
                  <div
                    data-testid="nav-menu-popup"
                    className="flex flex-col h-full bg-[rgba(30,42,34,0.92)] rounded-rounded p-6 gap-y-6"
                  >
                    <div className="flex justify-end" id="xmark">
                      <button
                        data-testid="close-menu-button"
                        onClick={close}
                        aria-label="إغلاق القائمة"
                      >
                        <XMark />
                      </button>
                    </div>

                    {/* Scrolls on its own, so the close button and the settings below stay in view. */}
                    <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar flex flex-col gap-y-8">
                      <ul className="flex flex-col gap-y-3">
                        <li>
                          <LocalizedClientLink
                            href="/"
                            className="text-2xl leading-[1.6] hover:text-brand-on-ink"
                            onClick={close}
                            data-testid="home-link"
                          >
                            الرئيسية
                          </LocalizedClientLink>
                        </li>
                        <li>
                          <LocalizedClientLink
                            href="/store"
                            className="text-2xl leading-[1.6] hover:text-brand-on-ink"
                            onClick={close}
                            data-testid="store-link"
                          >
                            كل المنتجات
                          </LocalizedClientLink>
                        </li>
                      </ul>

                      {categories.length > 0 && (
                        <div>
                          <p className={sectionTitle}>الأقسام</p>
                          <ul className="flex flex-col gap-y-4">
                            {categories.map((category) => (
                              <li key={category.id}>
                                <div className="flex items-center justify-between gap-x-3">
                                  <LocalizedClientLink
                                    href={`/categories/${category.handle}`}
                                    className="text-xl leading-[1.6] hover:text-brand-on-ink"
                                    onClick={close}
                                    data-testid="category-link"
                                  >
                                    {category.name}
                                  </LocalizedClientLink>
                                  {category.children.length > 0 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpanded((current) =>
                                          current === category.id
                                            ? null
                                            : category.id
                                        )
                                      }
                                      aria-expanded={expanded === category.id}
                                      aria-label={`أقسام ${category.name}`}
                                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-white/10"
                                      data-testid="category-toggle"
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        aria-hidden="true"
                                        className={clx(
                                          "transition-transform",
                                          expanded === category.id &&
                                            "rotate-180"
                                        )}
                                      >
                                        <path d="M3 4.5 L6 7.5 L9 4.5" />
                                      </svg>
                                    </button>
                                  )}
                                </div>
                                {category.children.length > 0 &&
                                  expanded === category.id && (
                                  <ul className="mt-2 flex flex-col gap-y-2 border-s border-white/15 ps-4">
                                    {category.children.map((child) => (
                                      <li key={child.id}>
                                        <LocalizedClientLink
                                          href={`/categories/${child.handle}`}
                                          className="text-base leading-[1.6] text-brand-sage-soft hover:text-white"
                                          onClick={close}
                                          data-testid="category-link"
                                        >
                                          {child.name}
                                        </LocalizedClientLink>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {brands.length > 0 && (
                        <div>
                          <p className={sectionTitle}>الماركات</p>
                          <ul className="flex flex-wrap gap-2">
                            {brands.map((brand) => (
                              <li key={brand.id}>
                                <LocalizedClientLink
                                  href={`/collections/${brand.handle}`}
                                  className="inline-flex items-center rounded-full border border-white/25 px-3 py-1.5 text-sm hover:bg-white/10"
                                  onClick={close}
                                >
                                  {brand.title}
                                </LocalizedClientLink>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <ul className="flex flex-col gap-y-2 border-t border-white/15 pt-6">
                        <li>
                          <LocalizedClientLink
                            href="/account"
                            className="text-base leading-[1.6] hover:text-brand-on-ink"
                            onClick={close}
                            data-testid="account-link"
                          >
                            حسابي
                          </LocalizedClientLink>
                        </li>
                        <li>
                          <LocalizedClientLink
                            href="/cart"
                            className="text-base leading-[1.6] hover:text-brand-on-ink"
                            onClick={close}
                            data-testid="cart-link"
                          >
                            السلة
                          </LocalizedClientLink>
                        </li>
                      </ul>
                    </div>

                    <div className="flex flex-col gap-y-6">
                      {!!locales?.length && (
                        <div
                          className="flex justify-between"
                          onMouseEnter={languageToggleState.open}
                          onMouseLeave={languageToggleState.close}
                        >
                          <LanguageSelect
                            toggleState={languageToggleState}
                            locales={locales}
                            currentLocale={currentLocale}
                          />
                          <ArrowRightMini
                            className={clx(
                              "transition-transform duration-150",
                              languageToggleState.state ? "-rotate-90" : ""
                            )}
                          />
                        </div>
                      )}
                      <div
                        className="flex justify-between"
                        onMouseEnter={countryToggleState.open}
                        onMouseLeave={countryToggleState.close}
                      >
                        {regions && (
                          <CountrySelect
                            toggleState={countryToggleState}
                            regions={regions}
                          />
                        )}
                        <ArrowRightMini
                          className={clx(
                            "transition-transform duration-150",
                            countryToggleState.state ? "-rotate-90" : ""
                          )}
                        />
                      </div>
                      <Text className="flex justify-between txt-compact-small">
                        © {new Date().getFullYear()} حلزونة. جميع الحقوق محفوظة.
                      </Text>
                    </div>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}

export default SideMenu
